import { supabase } from '../supabaseClient';

/**
 * Auto-archives resolved incidents that are older than 24 hours
 * This function checks for incidents with status 'resolved' and updated_at timestamp
 * older than 24 hours, then updates their status to 'archived'
 */
export async function autoArchiveResolvedIncidents() {
  try {
    console.log('Starting auto-archive process for resolved incidents...');

    // Calculate 24 hours ago timestamp
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

    // Query for resolved incidents older than 24 hours
    const { data: resolvedIncidents, error: fetchError } = await supabase
      .from('incident_reports')
      .select('id, type, location, details, updated_at, user_id')
      .eq('status', 'resolved')
      .lt('updated_at', twentyFourHoursAgo.toISOString());

    if (fetchError) {
      console.error('Error fetching resolved incidents:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!resolvedIncidents || resolvedIncidents.length === 0) {
      console.log('No resolved incidents found that are older than 24 hours');
      return { success: true, archivedCount: 0 };
    }

    console.log(`Found ${resolvedIncidents.length} resolved incident(s) to archive`);

    // Archive each incident
    const archivedIncidents = [];
    const auditLogs = [];

    for (const incident of resolvedIncidents) {
      // Update incident status to archived
      const { error: updateError } = await supabase
        .from('incident_reports')
        .update({ 
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .eq('id', incident.id);

      if (updateError) {
        console.error(`Error archiving incident ${incident.id}:`, updateError);
        continue;
      }

      archivedIncidents.push(incident);

      // Prepare audit log entry
      auditLogs.push({
        user_id: incident.user_id, // Use the original incident creator as the user
        action: 'auto-archived incident report',
        target_id: incident.id,
        target_type: 'IncidentReports',
        details: {
          type: incident.type,
          location: incident.location,
          reason: 'Auto-archived after 24 hours of being resolved',
          auto_archived_at: new Date().toISOString(),
          previous_status: 'resolved'
        }
      });
    }

    // Insert audit logs in batch
    if (auditLogs.length > 0) {
      const { error: auditError } = await supabase
        .from('audit_logs')
        .insert(auditLogs);

      if (auditError) {
        console.error('Error inserting audit logs:', auditError);
        // Don't fail the whole operation if audit logging fails
      }
    }

    console.log(`Successfully auto-archived ${archivedIncidents.length} incident(s)`);
    
    return { 
      success: true, 
      archivedCount: archivedIncidents.length,
      archivedIncidents: archivedIncidents.map(inc => inc.id)
    };

  } catch (error) {
    console.error('Unexpected error in auto-archive process:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Starts the auto-archive scheduler that runs every hour
 * Returns a cleanup function to stop the scheduler
 */
export function startAutoArchiveScheduler() {
  console.log('Starting auto-archive scheduler (runs every hour)');
  
  // Run immediately on start
  autoArchiveResolvedIncidents();
  
  // Then run every hour (3600000 milliseconds)
  const intervalId = setInterval(() => {
    autoArchiveResolvedIncidents();
  }, 3600000); // 1 hour

  // Return cleanup function
  return () => {
    console.log('Stopping auto-archive scheduler');
    clearInterval(intervalId);
  };
}

/**
 * Manual trigger for auto-archiving (useful for testing or admin actions)
 */
export async function manualTriggerAutoArchive() {
  console.log('Manual trigger for auto-archive initiated');
  const result = await autoArchiveResolvedIncidents();
  
  if (result.success) {
    const message = `Auto-archive completed successfully. Archived ${result.archivedCount} incident(s).`;
    console.log(message);
    return { success: true, message, ...result };
  } else {
    const message = `Auto-archive failed: ${result.error}`;
    console.error(message);
    return { success: false, message };
  }
}